// src/components/listing/ImagesForm.jsx
import React, { useState, useCallback } from 'react';
import { FiCamera, FiX, FiUploadCloud } from 'react-icons/fi';

export default function ImagesForm({
  images,
  errors,
  submitting,
  onImagesChange,
  onImageUploadProgress,
  imageUploadProgress
}) {
  const handleImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    
    // Validate files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        return false;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB
        alert(`${file.name} is too large. Max size is 5MB`);
        return false;
      }
      
      return true;
    });
    
    if (validFiles.length === 0) return;
    
    const newImages = [...images];
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push({
          id: Date.now() + Math.random(),
          uri: e.target.result,
          file: file
        });
        
        if (newImages.length === images.length + validFiles.length) {
          onImagesChange(newImages);
        }
      };
      reader.readAsDataURL(file);
    });
  }, [images, onImagesChange]);

  const removeImage = useCallback((index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  }, [images, onImagesChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const inputEvent = {
        target: {
          files: {
            length: files.length,
            item: (index) => files[index],
            [Symbol.iterator]: function* () {
              for (let i = 0; i < files.length; i++) {
                yield files[i];
              }
            }
          }
        }
      };
      handleImageSelect(inputEvent);
    }
  }, [handleImageSelect]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
          <FiCamera className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Photos</h3>
          <p className="text-gray-600 text-sm">Add photos of your item (up to 10)</p>
        </div>
      </div>

      {/* Image Upload Area */}
      <div 
        className={`border-2 border-dashed rounded-xl p-8 text-center mb-4 transition ${
          errors.images ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
        } ${submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !submitting && document.getElementById('image-upload').click()}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          id="image-upload"
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageSelect}
          disabled={submitting}
          className="hidden"
        />
        
        <div className="flex flex-col items-center justify-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FiUploadCloud className="w-8 h-8 text-gray-400" />
          </div>
          
          <p className="text-gray-700 font-medium mb-2">
            Click to upload or drag and drop
          </p>
          
          <p className="text-gray-500 text-sm">
            JPEG, PNG up to 5MB each
          </p>
          
          <p className="text-gray-400 text-xs mt-2">
            {images.length} of 10 photos uploaded
          </p>
        </div>
      </div>

      {errors.images && (
        <p className="text-sm text-red-600 mb-4">{errors.images}</p>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={image.id} className="relative group">
              <img
                src={image.uri}
                alt={`Listing image ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                disabled={submitting}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition"
              >
                <FiX className="w-3 h-3" />
              </button>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition rounded-lg" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}